import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import PropTypes from 'prop-types';

import { toggleModal, toggleModalWithTimer } from 'apps/auth/actions/appAction';
import { deleteCard } from 'apps/auth/actions/cardsAction';
import { deleteDCAccount } from 'apps/auth/actions/extraAction';
import { changeFormFieldValue, setNewForm } from 'apps/auth/actions/formAction';
import { InfoPage } from 'apps/auth/components/common/Layout/InfoPage/InfoPage';
import PopupButton from 'apps/auth/components/common/Popup/PopupButton';
import SimplePopup from 'apps/auth/components/common/Popup/SimplePopup';
import { Text } from 'apps/auth/components/common/Text/Text';
import Form from 'apps/auth/components/form-multi/Form';
import { getField } from 'apps/auth/components/form-multi/utils';
import Loading from 'apps/auth/components/utils/Loading';
import ModalComponent from 'apps/auth/components/utils/ModalComponent';
import { ACH_ACCOUNT, CARD, LEAN_ACCOUNT } from 'apps/auth/constants/Constants';
import { AUTH_CARD_DELETE } from 'apps/auth/constants/UrlConstants';
import { MarkdownService } from 'apps/auth/services/MarkdownService';

const FORM_NAME = 'deleteCardPopup';

const DeleteCardOrAccountPopup = (props) => {
    const { isACH = false, isLean = false, forDcAccount = false, force = false, popupRef = null, beforeSubmit } = props;
    const [errorMessage, setErrorMessage] = useState(null);

    const onClickNotNow = () => {
        props.toggleModal(false);
    };

    useEffect(() => {
        const selectedCard = props.selectedCard;
        const nameField = getField('name', selectedCard.alias, '', true, false, props.placeholder, 'text');
        const cardIdField = getField('id', selectedCard.id);
        const cardTypeField = getField('type', selectedCard.type);
        const forceField = getField('force', force);

        props.setNewForm(FORM_NAME, {
            [nameField.name]: nameField,
            [cardIdField.name]: cardIdField,
            [cardTypeField.name]: cardTypeField,
            [forceField.name]: forceField,
        });
    }, []);

    const isCard = () => {
        return props.selectedCard.type === CARD;
    };

    const onSubmitHandler = (fields, data) => {
        const forceError = data.extra.force;
        const successText = data?.messages?.successText;

        if (isACH && forceError) {
            popupRef.current?.close();
            props.toggleModal(
                true,
                <ModalComponent canCloseModal={false} isVerticalCentered>
                    <InfoPage
                        insideModalComponent={true}
                        animatedIconFail
                        mainText={forceError}
                        primaryButtonCaption={props.goBack}
                        primaryButtonOnClick={onClickNotNow}
                    />
                </ModalComponent>,
            );
        } else if (!forceError) {
            const dcAccountType = isACH ? ACH_ACCOUNT : LEAN_ACCOUNT;
            const action = forDcAccount ? props.deleteDCAccount(props.selectedCard.id, dcAccountType) : props.deleteCard(props.selectedCard.id);
            if (popupRef?.current) {
                action.then(() => popupRef.current?.close());
            } else {
                action.then(() => {
                    popupRef.current?.close();
                    if (isACH) {
                        props.toggleModalWithTimer(true, successText);
                    }
                });
            }
        } else {
            setErrorMessage(forceError);
            if (data.force) {
                props.changeFormFieldValue(FORM_NAME, data.force, 'force');
            }
        }
    };

    const getTitle = (ach = false, lean = false) => {
        const force = props.forms[FORM_NAME].fields['force'];
        if (force?.value) {
            return isCard() ? props.deleteCardTitle : ach || lean ? props.deleteAchAccountTitle : props.deleteAccountTitle;
        } else {
            return isCard() ? props.deleteForceCardTitle : ach || lean ? props.deleteAchAccountTitle : props.deleteForceAccountTitle;
        }
    };

    const getDeleteButtonTitle = () => {
        const force = props.forms[FORM_NAME].fields['force'];
        return force && force.value ? props.deleteForceButton : props.deleteButton;
    };

    const form = props.forms[FORM_NAME];
    if (!form) {
        return <Loading />;
    }
    const getText = (error) => {
        return (
            <>
                {MarkdownService.splitByParagraph(getTitle(isACH, isLean), props.selectedCard.alias)}
                {error && <Text color="error">{error}</Text>}
            </>
        );
    };

    const buttonACHLean = <PopupButton iconName={'cross'} type={'submit'} isAlertButton={true} text={props.revokeAccessText} />;

    const normalButtons = (
        <>
            <PopupButton iconName={'scissors'} tabIndex={'1002'} type={'submit'} text={getDeleteButtonTitle()} />
            <PopupButton
                iconName={'check'}
                text={isCard() ? props.keepCardButton : props.keepAccountButton}
                onClick={() => {
                    popupRef.current?.close();
                }}
            />
        </>
    );

    return (
        <Form
            id="delete_card_form_id"
            action={AUTH_CARD_DELETE}
            formName={FORM_NAME}
            validateForm={false}
            beforeSubmit={beforeSubmit}
            onSubmitHandler={onSubmitHandler}
        >
            <SimplePopup text={getText(errorMessage)} popupRef={popupRef}>
                {isACH || isLean ? buttonACHLean : normalButtons}
            </SimplePopup>
        </Form>
    );
};

const mapStateToProps = (state) => {
    return {
        forms: state.form,
        deleteCardTitle: state.messages.pageMessages.deleteCardTitle,
        deleteAccountTitle: state.messages.pageMessages.deleteAccountTitle,
        deleteAchAccountTitle: state.messages.pageMessages.deleteAchAccountTitle,
        deleteForceCardTitle: state.messages.pageMessages.deleteForceCardTitle,
        deleteForceAccountTitle: state.messages.pageMessages.deleteForceAccountTitle,
        deleteButton: state.messages.pageMessages.cardsDeleteButton,
        goBack: state.messages.pageMessages.goBack,
        deleteForceButton: state.messages.pageMessages.cardsForceDeleteButton,
        keepCardButton: state.messages.pageMessages.keepCardButton,
        keepAccountButton: state.messages.pageMessages.keepAccountButton,
        revokeAccessText: state.messages.pageMessages.revokeAccessText,
    };
};

DeleteCardOrAccountPopup.propsType = {
    popupRef: PropTypes.shape({ current: PropTypes.object }),
    beforeSubmit: PropTypes.func,
    isACH: PropTypes.bool,
    isLean: PropTypes.bool,
    forDcAccount: PropTypes.bool,
    force: PropTypes.bool,
};

export default connect(mapStateToProps, {
    toggleModal,
    setNewForm,
    changeFormFieldValue,
    deleteCard,
    deleteDCAccount,
    toggleModalWithTimer,
})(DeleteCardOrAccountPopup);
